import { useCallback, useMemo } from 'react';

import { MDText } from 'i18n-react';

import { useFeatureFlags } from 'Utilities/hooks';
import { Slug } from 'Utilities/profiling/@types';
import { isEnquiryExpirable } from 'Utilities/restricted';
import { Vehicle, VehicleLookupState } from 'Utilities/vehicles/@types';
import { EnquiryOfferResponse } from 'Utilities/vehicles/hooks/useGetEnquiryOffers';
import { useGetEnquiryStatusAndOffers } from 'Utilities/vehicles/hooks/useGetEnquiryStatusAndOffers';
import useGetVehicleEnquiry from 'Utilities/vehicles/hooks/useGetVehicleEnquiry';

import { LatestPostSaleOffer, PaymentStatuses } from '../../../payments/Payments.types';
import AccountTexts from '../../Pages/AccountPage/AccountPage.texts.json';

import { checkEnquiryActive, getPaymentCta } from './useVehicleCard.helpers';

const AccountT = new MDText(AccountTexts);

type RenderPriceOptions = {
  fallbackValuationPrice?: number | null;
  showEstimateOnLoad?: boolean;
};

export type UseVehicleCardPropsReturn = {
  ctaProps: {
    disabled: boolean;
    label: string;
    loading: boolean;
    to: string;
  };
  estimateData: {
    enquiryOffers?: EnquiryOfferResponse | null;
    enquiryStateSlug?: Slug | null;
    latestPostSaleOfferData?: LatestPostSaleOffer | null;
    paymentsStatuses?: PaymentStatuses | null;
  };
  gaEvent?: string;
  isEnquiryActive: boolean;
  isEnquiryExpirable: boolean;
  isEnquiryExpired: boolean;
  isOfferRestricted?: boolean;
  isSellingButtonLoading: boolean;
  renderPrice: (options?: RenderPriceOptions) => number | null | undefined;
};

export const useVehicleCard = (vehicle: Vehicle | VehicleLookupState): UseVehicleCardPropsReturn => {
  const { underOfferNewFlow } = useFeatureFlags();
  const { enquiry } = vehicle;
  const {
    enquiryOffers,
    enquiryStateSlug,
    gaEvent,
    isEnquiryOffersFetched,
    isEnquiryOffersLoading,
    isOfferRestricted,
    latestPostSaleOfferData,
    linkToContinueSelling,
    paymentsStatuses,
    primaryCtaLabel,
  } = useGetEnquiryStatusAndOffers({ vehicle });

  const { enquiry: latestVehicleEnquiry, isLoaded: isLatestVehicleEnquiryLoaded } = useGetVehicleEnquiry({ vehicle });

  const hasExpirableStatus = isEnquiryExpirable(enquiryStateSlug);
  const isEnquiryActive = checkEnquiryActive(enquiry);
  const isEnquiryExpired = hasExpirableStatus && !isEnquiryActive;

  const isSellingButtonLoading = useMemo(() => {
    if (!isLatestVehicleEnquiryLoaded) {
      return true;
    }

    const hasEligibleEnquiry = enquiry?.marketplaceEligible?.eligible && enquiry.dpEnquiryId;
    const isOffersLoading = isEnquiryOffersLoading || !isEnquiryOffersFetched;

    return Boolean(hasEligibleEnquiry && isOffersLoading);
  }, [enquiry, isEnquiryOffersLoading, isEnquiryOffersFetched, isLatestVehicleEnquiryLoaded]);

  const label = useMemo(() => {
    const paymentsCTA = getPaymentCta(paymentsStatuses, latestPostSaleOfferData);
    if (paymentsCTA) {
      return paymentsCTA;
    }

    if (enquiryOffers) {
      return AccountT.translate(underOfferNewFlow ? 'confirmSaleLabel' : 'seeOffersLabel');
    }

    if (isEnquiryExpired) {
      return AccountT.translate('expiredEnquiry');
    }

    return primaryCtaLabel;
  }, [
    enquiryOffers,
    latestPostSaleOfferData,
    paymentsStatuses,
    primaryCtaLabel,
    underOfferNewFlow,
    isEnquiryExpired,
  ]);

  const renderPrice = useCallback((options?: RenderPriceOptions) => {
    if (isLatestVehicleEnquiryLoaded) {
      return latestPostSaleOfferData?.amount
        || enquiryOffers?.closingBid?.value
        || latestVehicleEnquiry?.displayPrice
        || latestVehicleEnquiry?.sellerWillAcceptPrice
        || latestVehicleEnquiry?.priceHigh
        || options?.fallbackValuationPrice
        || vehicle?.enquiry?.valuation?.estimate;
    }

    if (options?.showEstimateOnLoad) {
      return vehicle?.enquiry?.valuation?.estimate;
    }

    return null;
  }, [
    isLatestVehicleEnquiryLoaded,
    latestPostSaleOfferData?.amount,
    enquiryOffers?.closingBid?.value,
    latestVehicleEnquiry?.displayPrice,
    latestVehicleEnquiry?.sellerWillAcceptPrice,
    latestVehicleEnquiry?.priceHigh,
    vehicle?.enquiry?.valuation?.estimate,
  ]);

  return {
    ctaProps: {
      disabled: isSellingButtonLoading,
      label,
      loading: isSellingButtonLoading,
      to: linkToContinueSelling,
    },
    estimateData: {
      enquiryOffers,
      enquiryStateSlug,
      latestPostSaleOfferData,
      paymentsStatuses,
    },
    gaEvent,
    isEnquiryActive,
    isEnquiryExpirable: hasExpirableStatus,
    isEnquiryExpired,
    isOfferRestricted,
    isSellingButtonLoading,
    renderPrice,
  };
};
